import * as React from 'react';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { CloseCircleFilled, ArrowRightOutlined, MobileOutlined, QrcodeOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import { Link, useRouteMatch } from 'react-router-dom';
import { antGrey, hexStringToRGBAString, runwayColors } from './styles/globalStyles';
import Button from 'antd/lib/button';
import { RegisterDeviceInstructionsSheet } from './RegisterDeviceInstructions';
import { createUserSettingsRoute } from '../helpers/routeHelpers';
import Popover from 'antd/lib/popover';
import { QRCode } from 'react-qrcode-logo';
import { getRegisterDeviceDismissedForAppId, setRegisterDeviceDismissedForAppId } from '../helpers/localStorageHelper';
import { useIsIOSDevice, useIsMobile } from '@/mediaQueriesHelpers';
import routes from '@/routes';
import { useSelector } from 'react-redux';
import { selectAppSettings, selectUserDevices } from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import { getIdFromAppId } from '@/helpers';

export const REGISTER_DEVICE_URL_PARAM = 'registerDevice';

export const generateUserSettingsLink = () => {
    const userSettingsLink = createUserSettingsRoute();

    return `${userSettingsLink}?${REGISTER_DEVICE_URL_PARAM}=true`;
};

export interface RegisterDeviceMobileAlertProps {
    isClosable?: boolean;
    isInline?: boolean;
    shouldShowActionButton?: boolean;
    shouldStartOpen?: boolean;
}

export const RegisterDeviceMobileAlert: React.FC<RegisterDeviceMobileAlertProps> = ({
    isClosable = true,
    isInline = false,
    shouldShowActionButton,
    shouldStartOpen,
}) => {
    const isMobile = useIsMobile();
    const isIos = useIsIOSDevice();

    const recentBuildsRoute = useRouteMatch({ path: routes.appBuilds, exact: true });
    const appIdParam = recentBuildsRoute?.params['appIdParam'];
    const orgIdParam = recentBuildsRoute?.params['orgIdParam'];

    const shouldHideRegisterDevicePrompt = useSelector((state: RootState) => {
        const appId = getIdFromAppId(orgIdParam, appIdParam, state);

        if (appId) {
            return selectAppSettings(state, appId)?.shouldHideRegisterDevicePrompt;
        }

        return false;
    });

    const devices = useSelector((state: RootState) => selectUserDevices(state));

    const [isRegisterDeviceInstructionsVisible, setIsRegisterDeviceInstructionsVisible] = React.useState(
        shouldStartOpen ?? false,
    );

    const isRegisterDeviceAlertDismissed = appIdParam ? getRegisterDeviceDismissedForAppId(appIdParam) : false;

    const shouldShowRegisterDeviceAlert =
        devices.length === 0 &&
        isMobile &&
        isIos &&
        recentBuildsRoute &&
        !isRegisterDeviceAlertDismissed &&
        !shouldHideRegisterDevicePrompt;

    const userSettingsLink = generateUserSettingsLink();

    return (
        <>
            <RegisterDeviceInstructionsSheet
                isOpen={isRegisterDeviceInstructionsVisible}
                onClose={() => {
                    setIsRegisterDeviceInstructionsVisible(false);
                }}
            />
            {shouldShowRegisterDeviceAlert && (
                <>
                    <Alert
                        style={{
                            padding: 16,
                            paddingBottom: shouldShowActionButton ? 16 : 12,
                            borderColor: isInline ? hexStringToRGBAString(runwayColors.primary, 0.25) : undefined,
                        }}
                        description={
                            <Space direction="vertical">
                                <Space size={16} align={shouldShowActionButton ? 'center' : 'start'}>
                                    <MobileOutlined
                                        style={{
                                            marginTop: shouldShowActionButton ? 0 : 4,
                                            fontSize: 24,
                                            color: runwayColors.primary,
                                        }}
                                    />
                                    <Space direction="vertical">
                                        <Text
                                            style={{
                                                fontSize: 16,
                                                color: antGrey.grey8,
                                            }}
                                        >
                                            Register your device to start testing your team’s builds
                                        </Text>
                                        {!shouldShowActionButton && (
                                            <Link
                                                to={userSettingsLink}
                                                style={{
                                                    fontSize: 14,
                                                }}
                                            >
                                                Enroll this device with Build Distro now -{'>'}
                                            </Link>
                                        )}
                                    </Space>
                                </Space>
                                {shouldShowActionButton && (
                                    <Button
                                        style={{
                                            width: '100%',
                                            marginTop: 4,
                                        }}
                                        type="primary"
                                        icon={<ArrowRightOutlined />}
                                        onClick={() => {
                                            setIsRegisterDeviceInstructionsVisible(true);
                                        }}
                                    >
                                        Tap here to get started
                                    </Button>
                                )}
                            </Space>
                        }
                        type="info"
                        closeIcon={
                            <CloseCircleFilled
                                style={{
                                    opacity: 0.5,
                                }}
                            />
                        }
                        closable={isClosable}
                        onClose={() => setRegisterDeviceDismissedForAppId(appIdParam, true)}
                    />
                </>
            )}
        </>
    );
};

export interface RegisterDeviceAlertProps {
    isInline?: boolean;
    style?: React.CSSProperties;
}

export const RegisterDeviceAlert: React.FC<RegisterDeviceAlertProps> = ({ isInline = false, style }) => {
    const userSettingsLink = generateUserSettingsLink();

    return (
        <Alert
            style={{
                marginBottom: 24,
                borderColor: hexStringToRGBAString(runwayColors.primary, 0.25),
                alignItems: 'center',
                ...style,
            }}
            description={<Text>Register your device to start testing your team’s Ad Hoc or dev builds.</Text>}
            type="info"
            icon={<MobileOutlined />}
            showIcon
            action={
                <Popover content={<QRCode value={`${window.location.origin}${userSettingsLink}`} size={200} />}>
                    <Button type="primary" icon={<QrcodeOutlined />}>
                        {isInline ? 'Scan this QR code' : 'Scan this QR code to get started'}
                    </Button>
                </Popover>
            }
        />
    );
};
